// run-command-interrupted-outcome-override.ts
//
// Phase 05 (AC-02, N1/N2/N3 final_rescan): once Ctrl-C/SIGTERM sets the
// interrupt flag, `run`'s wiring must override whatever `runMissionToIdle`
// returned (or threw) to outcome "interrupted" — halt-step may not have had
// a chance to see the flag before a spawned git process died from the SAME
// signal (spawnAsyncCapture defaults to forwardSigint:true across ~15 call
// sites, N1 in the design doc). Split out of run-command-cli-wiring.ts to
// keep that file under its 200-line budget (N6 vòng 2).
import { randomUUID } from "node:crypto";
import { MachineLockBusyError, MachineLockRecoveringError } from "./mission-lock.js";
import { MissionStore } from "./mission-store.js";
import type { RunMissionResult } from "./supervisor-mission-run.js";

export interface ApplyInterruptedOutcomeOverrideInput {
  controlRoot: string;
  isInterrupted: () => NodeJS.Signals | undefined;
  log: { error: (msg: string) => void };
  run: () => Promise<RunMissionResult>;
}

/**
 * Runs `runMissionToIdle` (via `input.run`) and, if the interrupt flag ends
 * up set — whether it returned normally or threw — forces the outcome to
 * "interrupted", journaling the event itself when halt-step didn't already
 * (never double-commits). Lock-contention errors from a run that never held
 * the lock (MachineLockBusyError/RecoveringError) are never swallowed —
 * those carry a more useful message than a bare "interrupted" would.
 */
export async function applyInterruptedOutcomeOverride(
  input: ApplyInterruptedOutcomeOverrideInput,
): Promise<RunMissionResult> {
  let result: RunMissionResult;
  let alreadyJournaled = false;
  try {
    result = await input.run();
    alreadyJournaled = result.outcome === "interrupted";
  } catch (err) {
    const sig = input.isInterrupted();
    if (!sig || err instanceof MachineLockBusyError || err instanceof MachineLockRecoveringError) {
      throw err;
    }
    result = { outcome: "interrupted", ticksRun: 0 };
  }

  const sig = input.isInterrupted();
  if (!sig || alreadyJournaled) return result;

  try {
    const store = await MissionStore.open(input.controlRoot);
    try {
      await store.commit([
        {
          event_id: randomUUID(),
          delta: { kind: "mission_status_changed", status: "interrupted", signal: sig } as never,
        },
      ]);
    } finally {
      await store.close();
    }
  } catch (err) {
    input.log.error(
      `Không ghi được event interrupted: ${err instanceof Error ? err.message : String(err)}`,
    );
  }
  return { ...result, outcome: "interrupted" };
}
